import type { Route } from '../../types';
import type { CoordinationFleetContext, RelatedRouteSnapshot } from './types';

const ACTIVE_STATUSES = new Set([
  'CREATED',
  'ASSIGNED',
  'IN_PROGRESS',
  'IN_TRANSIT',
  'created',
  'assigned',
  'in_progress',
  'in_transit',
  'IN-PROGRESS'
]);

function normalizeStatus(status: unknown): string {
  return String(status || '').trim().toUpperCase().replace(/-/g, '_');
}

function isActiveRoute(status: unknown): boolean {
  const n = normalizeStatus(status);
  return ACTIVE_STATUSES.has(n) || ACTIVE_STATUSES.has(String(status || '').trim());
}

function isCompletedRoute(status: unknown): boolean {
  const n = normalizeStatus(status);
  return n === 'COMPLETED' || n === 'CANCELLED';
}

export function toRelatedRouteSnapshot(route: Route): RelatedRouteSnapshot {
  return {
    routeId: route.id,
    routeNumber: route.number?.trim() || String(route.id),
    status: String(route.status ?? ''),
    driverId: route.driverId ?? null,
    vehicleId: route.vehicleId ?? null,
    plannedLoadingDate: route.plannedLoadingDate ?? null,
    plannedUnloadingDate: route.plannedUnloadingDate ?? null,
    executionComplete: isCompletedRoute(route.status)
  };
}

/** Build lightweight fleet context for coordination (capped, no recursive chains). */
export function buildCoordinationFleetContext(
  route: Route,
  allRoutes: Route[] = []
): CoordinationFleetContext {
  const driverId = route.driverId ?? null;
  const vehicleId = route.vehicleId ?? null;

  const relatedRoutes: RelatedRouteSnapshot[] = [];
  const seen = new Set<number>();

  for (const other of allRoutes) {
    if (other.id === route.id || seen.has(other.id)) continue;
    if (isCompletedRoute(other.status)) continue;

    const sameDriver = driverId != null && Number(other.driverId) === Number(driverId);
    const sameVehicle = vehicleId != null && Number(other.vehicleId) === Number(vehicleId);
    if (!sameDriver && !sameVehicle) continue;
    if (!isActiveRoute(other.status) && !sameVehicle) continue;

    seen.add(other.id);
    relatedRoutes.push(toRelatedRouteSnapshot(other));
    if (relatedRoutes.length >= 12) break;
  }

  relatedRoutes.sort((a, b) => {
    const ta = a.plannedLoadingDate ? new Date(a.plannedLoadingDate).getTime() : 0;
    const tb = b.plannedLoadingDate ? new Date(b.plannedLoadingDate).getTime() : 0;
    return ta - tb;
  });

  return {
    currentRouteId: route.id,
    driverId,
    vehicleId,
    trailerVehicleId: route.trailerVehicleId ?? null,
    relatedRoutes
  };
}
